import React, { useRef, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Badge,
  Skeleton,
  DefaultAvatar,
  TwitterBlueVerified,
  Tooltip
  // @ts-ignore
} from '@netbasequid/core'
import { Theme } from 'features/ui/types'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { getTooltipContent } from 'components/widget/AuthorsWidgets/share/Table/utils'
import {
  StyledContainer,
  StyledAuthorNameContainer,
  StyledAuthorLink,
  StyledContent,
  StyledIconContainer,
  StyledAuthorName,
  StyledAuthorHandleContainer,
  StyledAuthorHandle,
  StyledAvatarLink
} from './index.css'
import { getDataSource, getSourceLogo } from './utils'

export const AVATAR_DIAMETER = '48px'
export const BADGE_DIAMETER = '14px'

interface AvatarWithBadgeProps {
  theme: Theme
  row: InsightData
}

export const AvatarWithBadge = ({ theme, row }: AvatarWithBadgeProps) => {
  const source = getDataSource(row)
  const logo = getSourceLogo(source!, theme)
  const tooltipContent = getTooltipContent(source, row)
  const isDisplayLogo = Boolean(logo)

  const displayDefaultFallbackAvatar = (rowData: InsightData) =>
    !rowData.authorName && !rowData.authorHandle && !rowData.authorAvatarUrl

  return (
    <Badge
      mode={theme}
      content={
        <StyledAvatarLink
          href={row.authorLink}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => {
            event.stopPropagation()
          }}>
          <Avatar
            variant="circular"
            diameter={BADGE_DIAMETER}
            mode={theme}
            alt={source}
            fallbackStyles={{
              fontSize: '10px',
              fontWeight: 700
            }}
            tooltip={tooltipContent}>
            {isDisplayLogo ? logo : null}
          </Avatar>
        </StyledAvatarLink>
      }>
      {displayDefaultFallbackAvatar(row) ? (
        <DefaultAvatar fontSize={48} mode={theme} />
      ) : (
        <StyledAvatarLink
          href={row.authorLink}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => {
            event.stopPropagation()
          }}>
          <Avatar
            mode={theme}
            src={row.authorAvatarUrl}
            alt={row.authorName || row.authorHandle || 'author-name-avatar'}
            variant="circular"
            fallbackStyles={{
              fontSize: '24px',
              fontWeight: 400
            }}
            diameter={AVATAR_DIAMETER}
            loader={
              <Skeleton
                variation="circle"
                width={AVATAR_DIAMETER}
                height={AVATAR_DIAMETER}
                animation="wave"
              />
            }
          />
        </StyledAvatarLink>
      )}
    </Badge>
  )
}

export const AuthorNameRow = ({ row, theme }: { row: InsightData; theme: Theme }) => {
  const cellContentRef = useRef<HTMLAnchorElement | HTMLDivElement>(null)
  const [displayTooltip, setDisplayTooltip] = useState(false)
  const { t } = useTranslation()

  const checkCellContent = useCallback(() => {
    if (!cellContentRef.current) return
    const cellContent = cellContentRef.current
    setDisplayTooltip(cellContent.scrollHeight! > 22)
  }, [])

  useEffect(() => {
    checkCellContent()
  })

  return (
    <StyledAuthorNameContainer>
      {row.authorLink ? (
        <>
          <StyledAuthorLink
            href={row.authorLink}
            target="_blank"
            mode={theme}
            ref={cellContentRef as React.RefObject<HTMLAnchorElement>}
            onClick={(event) => {
              event.stopPropagation()
            }}>
            <Tooltip
              mode={theme}
              enabled={displayTooltip}
              content={row.authorName || row.authorHandle}>
              <StyledContent>{row.authorName || row.authorHandle}</StyledContent>
            </Tooltip>
          </StyledAuthorLink>
          {row.authorVerified && (
            <StyledIconContainer>
              <Tooltip mode={theme} content={t('verifiedAccount')}>
                <TwitterBlueVerified fontSize={16} />
              </Tooltip>
            </StyledIconContainer>
          )}
        </>
      ) : (
        <StyledAuthorName mode={theme} ref={cellContentRef as React.RefObject<HTMLDivElement>}>
          <Tooltip
            mode={theme}
            enabled={displayTooltip}
            content={row.authorName || row.authorHandle}>
            <StyledContent>{row.authorName || row.authorHandle}</StyledContent>
          </Tooltip>
          {row.authorVerified && (
            <StyledIconContainer>
              <Tooltip mode={theme} content={t('verifiedAccount')}>
                <TwitterBlueVerified fontSize={16} />
              </Tooltip>
            </StyledIconContainer>
          )}
        </StyledAuthorName>
      )}
    </StyledAuthorNameContainer>
  )
}

export const AuthorHandleRow = ({ row, theme }: { row: InsightData; theme: Theme }) => {
  const handleContentRef = useRef<HTMLDivElement>(null)
  const [displayTooltip, setDisplayTooltip] = useState(false)

  const checkContent = useCallback(() => {
    if (!handleContentRef.current) return
    const handleContent = handleContentRef.current
    setDisplayTooltip(handleContent.scrollHeight! > handleContent.clientHeight!)
  }, [])

  useEffect(() => {
    checkContent()
  })
  return (
    <StyledAuthorHandleContainer ref={handleContentRef as React.RefObject<HTMLDivElement>}>
      <Tooltip mode={theme} enabled={displayTooltip} content={row.authorHandle}>
        <StyledAuthorHandle mode={theme}>{row.authorHandle}</StyledAuthorHandle>
      </Tooltip>
    </StyledAuthorHandleContainer>
  )
}

export const NameCell = ({ row, theme }: { row: InsightData; theme: Theme }) => (
  <StyledContainer>
    <AuthorNameRow row={row} theme={theme} />
    {row.authorName && row.authorHandle && <AuthorHandleRow row={row} theme={theme} />}
  </StyledContainer>
)
